import BaseService, { SLUG_TOKEN } from '../core/BaseService';

export default class SurveysService extends BaseService {
  static async getPublicSurveyByUUID(uuid: string, config_uuid: string) {
    return await super.request(`/surveys/${uuid}${config_uuid}`);
  }

  static async getSurveyByUUID(
    clientSlug: string,
    uuid: string,
    config_uuid: string = ''
  ) {
    return await super.request(
      `/clients/${clientSlug || SLUG_TOKEN}/surveys/${uuid}${config_uuid}`
    );
  }

  static async getSurveys(
    clientSlug: string,
    query: string,
    status: string,
    page: number,
    pageSize: number,
    organizationalUnit?: number
  ) {
    return await super.request(
      `/clients/${clientSlug || SLUG_TOKEN}/surveys?q=${
        query ? encodeURIComponent(query) : ''
      }&status=${status}&limit=${pageSize}&skip=${pageSize * (page - 1)}&ou=${
        organizationalUnit ?? ''
      }`
    );
  }

  static async getCategories() {
    return await super.request(`/surveys/categories`);
  }

  static async saveResponse(surveyId: number, configId: number, qs?: string) {
    return await super.request(
      `/surveys/${surveyId}/responses/${configId}${qs ?? ''}`,
      'POST'
    );
  }

  static async finishResponse(surveyId: number, responseId: number) {
    return await super.request(
      `/surveys/${surveyId}/responses/${responseId}/finish`,
      'POST'
    );
  }

  static async saveQuestionResponse(
    surveyId: number,
    responseId: number,
    questionId: number,
    time: number,
    text?: string,
    value?: number,
    options?: number[]
  ) {
    return await super.request(
      `/surveys/${surveyId}/responses/${responseId}/questions`,
      'POST',
      {
        question_id: questionId,
        text,
        value,
        options,
        time,
      }
    );
  }

  static async getResponses(clientSlug: string, surveyId: number) {
    return await super.request(
      `/clients/${clientSlug || SLUG_TOKEN}/surveys/${surveyId}/responses`
    );
  }

  static async getQuestionResponses(
    clientSlug: string,
    surveyId: number,
    questionId: number,
    filters: any
  ) {
    const qs = Object.keys(filters || {}).reduce(
      (prev, next) =>
        prev ? `${prev}&${next}=${filters[next]}` : `${next}=${filters[next]}`,
      ''
    );
    return await super.request(
      `/clients/${
        clientSlug || SLUG_TOKEN
      }/surveys/${surveyId}/responses/questions_n/${questionId}?${qs}`
    );
  }

  static async getTextResponsesPaged(
    clientSlug: string,
    surveyId: number,
    questionId: number,
    offset: number,
    filters: any
  ) {
    const qs = Object.keys(filters || {}).reduce(
      (prev, next) =>
        prev ? `${prev}&${next}=${filters[next]}` : `${next}=${filters[next]}`,
      ''
    );
    return await super.request(
      `/clients/${
        clientSlug || SLUG_TOKEN
      }/surveys/${surveyId}/responses/questions_n/${questionId}/next?offset=${offset}&${qs}`
    );
  }

  static async getSurveyStatistics(
    clientSlug: string,
    surveyId: number,
    filters: any
  ) {
    const qs = Object.keys(filters || {}).reduce(
      (prev, next) =>
        prev ? `${prev}&${next}=${filters[next]}` : `${next}=${filters[next]}`,
      ''
    );

    return await super.request(
      `/clients/${
        clientSlug || SLUG_TOKEN
      }/surveys/${surveyId}/responses/stats?${qs}`
    );
  }

  static async getSurveyDashboards(clientSlug: string, surveyId: number) {
    return await super.request(
      `/clients/${
        clientSlug || SLUG_TOKEN
      }/surveys/${surveyId}/responses/dashboards`
    );
  }

  static async getDashboardData(
    clientSlug: string,
    surveyId: number,
    dashboardId: number,
    filters: any
  ) {
    const qs = Object.keys(filters || {}).reduce(
      (prev, next) =>
        prev ? `${prev}&${next}=${filters[next]}` : `${next}=${filters[next]}`,
      ''
    );
    return await super.request(
      `/clients/${
        clientSlug || SLUG_TOKEN
      }/surveys/${surveyId}/responses/dashboard/${dashboardId}?${qs}`
    );
  }

  static async getFirstYear(clientSlug: string, surveyId: number) {
    return await super.request(
      `/clients/${
        clientSlug || SLUG_TOKEN
      }/surveys/${surveyId}/responses/start_year`
    );
  }

  static async getSurveyTags(clientSlug: string, surveyId: number) {
    return await super.request(
      `/clients/${clientSlug || SLUG_TOKEN}/surveys/${surveyId}/tags`
    );
  }

  static async exportReport(
    clientSlug: string,
    surveyId: number,
    year: number,
    filters?: any
  ): Promise<Blob> {
    const qs = Object.keys(filters || {}).reduce(
      (prev, next) =>
        prev ? `${prev}&${next}=${filters[next]}` : `${next}=${filters[next]}`,
      ''
    );
    return await super.request(
      `/clients/${
        clientSlug || SLUG_TOKEN
      }/surveys/${surveyId}/responses/export?y=${year}&${qs}`
    );
  }

  static async getConfigs(
    clientSlug: string,
    surveyUID: string,
    page: number,
    pageSize: number,
    query?: string
  ) {
    return await super.request(
      `/clients/${
        clientSlug || SLUG_TOKEN
      }/surveys/${surveyUID}/configs?limit=${pageSize}&skip=${
        pageSize * (page - 1)
      }&${query}`
    );
  }

  static async downloadQRs(surveyUID: string, query?: string): Promise<Blob> {
    return await super.request(`/surveys/qrs/${surveyUID}?${query}`);
  }

  static getImagePath(clientSlug: string): string {
    return super.getFullPath(`/surveys/${clientSlug || SLUG_TOKEN}/logo`);
  }
}
